import omit from 'lodash/omit';
import { defineStore } from 'pinia';

import { getPersistingErrorActions } from '@evidentid/dashboard-commons/modules/persisting-error';
import type { GatewayApiClient } from '@evidentid/gateway-api-client';
import { VerificationDocument } from '@evidentid/gateway-api-client/src/models/submission-tprm/VerificationDocument.model';
import Logger from '@evidentid/logger';
import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
import Tracker from '@evidentid/tracker';
import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

interface DocumentResult {
    status: OperationStatus;
    content: VerificationDocument[];
}

export interface EntityDocumentsRequirements {
    gatewayClient: GatewayApiClient;
    tracker?: Tracker<any>;
    logger?: Logger<any>;
}

export interface EntityDocumentsState {
    rprDocuments: Record<string, Record<string, DocumentResult>>;
    entityDocuments: Record<string, Record<string, DocumentResult>>;
}

// The legacy module fired these through app.observer.onMutation() hooks in
// its module-builder registration; inlined at the same transition points.
function trackRprDocuments(app: EntityDocumentsRequirements, phase: string, rpName: string, email: string): void {
    const phaseName = phase.toLowerCase();
    app.tracker?.track({
        name: `Entities RprDocuments ${phaseName}`,
        description: phase === 'Finished'
            ? `Entities (${rpName}: ${email}) RprDocuments finished (0)`
            : `Entities (${rpName}: ${email}) RprDocuments ${phaseName}`,
        rpName,
        email,
        ...(phase === 'Finished' ? { count: 0 } : {}),
    });
    app.logger?.addBreadcrumb({
        category: 'entity-rpr-documents',
        message: `[${phase}] Entities RprDocuments`,
        data: phase === 'Finished' ? { rpName, email, types: [] } : { rpName, email },
    });
}

export const useEntityDocumentsStore = defineStore('entityDocuments', {
    state: (): EntityDocumentsState => ({
        rprDocuments: {},
        entityDocuments: {},
    }),
    actions: {
        startLoadingRprDocument(payload: { rpName: string, email: string }) {
            trackRprDocuments(this.$app as EntityDocumentsRequirements, 'Requested', payload.rpName, payload.email);
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...this.rprDocuments[payload.rpName],
                    [payload.email]: {
                        status: OperationStatus.loading,
                        content: [],
                    },
                },
            };
        },
        failLoadingRprDocument(payload: { rpName: string, email: string }) {
            trackRprDocuments(this.$app as EntityDocumentsRequirements, 'Failed', payload.rpName, payload.email);
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...this.rprDocuments[payload.rpName],
                    [payload.email]: {
                        status: OperationStatus.error,
                        content: [],
                    },
                },
            };
        },
        finishLoadingRprDocument(payload: { rpName: string, email: string, content: VerificationDocument[] }) {
            trackRprDocuments(this.$app as EntityDocumentsRequirements, 'Finished', payload.rpName, payload.email);
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: {
                    ...this.rprDocuments[payload.rpName],
                    [payload.email]: {
                        status: OperationStatus.success,
                        content: payload.content,
                    },
                },
            };
        },
        clearRprDocumentDetails(payload: { rpName: string, email: string }) {
            this.rprDocuments = {
                ...this.rprDocuments,
                [payload.rpName]: omit({
                    ...this.rprDocuments[payload.rpName],
                }, payload.email),
            };
        },
        startLoadingEntityDocument(payload: { rpName: string, documentId: string }) {
            this.entityDocuments = {
                ...this.entityDocuments,
                [payload.rpName]: {
                    ...this.entityDocuments[payload.rpName],
                    [payload.documentId]: { status: OperationStatus.loading, content: [] },
                },
            };
        },
        finishLoadingEntityDocument(payload: { rpName: string, documentId: string, content: VerificationDocument[] }) {
            this.entityDocuments = {
                ...this.entityDocuments,
                [payload.rpName]: {
                    ...this.entityDocuments[payload.rpName],
                    [payload.documentId]: { status: OperationStatus.success, content: payload.content },
                },
            };
        },
        failLoadingEntityDocument(payload: { rpName: string, documentId: string }) {
            this.entityDocuments = {
                ...this.entityDocuments,
                [payload.rpName]: {
                    ...this.entityDocuments[payload.rpName],
                    [payload.documentId]: { status: OperationStatus.error, content: [] },
                },
            };
        },
        clearEntityDocument(payload: { rpName: string, documentId: string }) {
            this.entityDocuments = {
                ...this.entityDocuments,
                [payload.rpName]: omit({ ...this.entityDocuments[payload.rpName] }, payload.documentId),
            };
        },
        async loadRprDocumentDetails(
            payload: {
                rpName: string;
                email: string;
                verificationId: string;
                instanceId?: string;
                requirement: TprmRequirementType;
            },
        ) {
            const { rpName, email, verificationId, instanceId, requirement } = payload;
            const { gatewayClient } = this.$app as EntityDocumentsRequirements;
            try {
                this.startLoadingRprDocument({ rpName, email });
                const { documents } = await gatewayClient.submissionTprmUi().getDocuments({
                    cn: rpName,
                    verId: verificationId,
                    requirement,
                    instanceId,
                });
                this.finishLoadingRprDocument({ rpName, email, content: documents || [] });
            } catch (error) {
                console.warn('GET Rpr Documents error', error);
                // TODO(PRODUCT-25362): remove this guard after BE stops returning `not-found`
                // for submission-history document requests on deactivated entities.
                if ((error as { reason?: string }).reason === 'not-found') {
                    this.failLoadingRprDocument({ rpName, email });
                    return;
                }
                await getPersistingErrorActions().showError(error);
                this.failLoadingRprDocument({ rpName, email });
            }
        },
        async loadEntityDocument(payload: { rpName: string, enityId: string, documentId: string }) {
            const { rpName, enityId, documentId } = payload;
            const { gatewayClient } = this.$app as EntityDocumentsRequirements;
            try {
                this.startLoadingEntityDocument({ rpName, documentId });
                const { filename, mimeType, document } = await gatewayClient.submissionTprmUi().getEntityDocument(
                    rpName,
                    enityId,
                    documentId,
                );
                this.finishLoadingEntityDocument({
                    rpName,
                    documentId,
                    content: [ { mimeType, data: document, filename } ],
                });
            } catch (error) {
                await getPersistingErrorActions().showError(error);
                this.failLoadingEntityDocument({ rpName, documentId });
            }
        },
    },
});
